import React, { FC, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import * as api from "api/datatable";
import { useGalaxieCallbackListener } from "containers/galaxy/GalaxieContextListener";
import { DatatableFocusState } from "types/Focus";
import { Pojo } from "types/Galaxy";
import { CancelTokenSource } from "axios";
import { RSQLCriteria } from "rsql-criteria-typescript";
import { uuidv4 } from "utils/uuid.utils";
import toaster from "composants/toaster/toaster";
import { ComponentState } from "types/Component";
import { FilterBar, PagedResource } from "types/Search";
import {
  ColumnMovedEvent,
  ColumnResizedEvent,
  FilterModel,
  SortModelItem,
  ColumnState,
  ColumnVisibleEvent,
} from "ag-grid-community";
import { GroupByFunctionParameter } from "types/InteractiveReport";
import { NotificationGroup } from "@axin-org/comet";
import AgGridContainer, { MIN_BATCH_SIZE } from "./AgGridContainer";
import { ReadOnlyTableProps } from "./Grid.types";
import { AgGridReact } from "ag-grid-react";
import { produce } from "immer";
import { DTReducer, initialDTState } from "./GridDtReducer";
import { getRSQLFilter, loadDatatableDefinition, fetchData } from "./utils/queryUtils";
import { t } from "utils/i18n";

const ReadOnlyTable: FC<ReadOnlyTableProps> = ({
  sjmoCode,
  tableName,
  ctrlkey,
  nodeType,
  processIdDrop,
  additionnalClause,
  onRowClickParent,
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const [definition, dispatchDefinition] = useReducer(DTReducer, initialDTState);
  const [localGroupByState, setLocalGroupByState] = useState<{
    columns: string[];
    functionList: GroupByFunctionParameter[];
  }>({ columns: [], functionList: [] });
  const [localDefaultOpenBreakRows, setLocalDefaultOpenBreakRows] = useState<boolean | undefined>(
    undefined,
  );

  const [localPinnedState, setLocalPinnedState] = useState<ColumnState[]>([]);

  const currentFocus = useMemo(
    () => definition.focus.find((f) => f.focusId === definition.selectedFocus),
    [definition.focus, definition.selectedFocus],
  );

  const effectiveGroupBy = useMemo(() => {
    return {
      columns: [...localGroupByState.columns, ...(currentFocus?.groupByColumns ?? [])],
      functionList: [
        ...localGroupByState.functionList,
        ...(currentFocus?.groupByFunctionList ?? []),
      ],
    };
  }, [localGroupByState, currentFocus?.groupByColumns, currentFocus?.groupByFunctionList]);

  const effectiveDefaultOpenBreakRows = useMemo(() => {
    return localDefaultOpenBreakRows ?? currentFocus?.defaultOpenBreakRows ?? false;
  }, [localDefaultOpenBreakRows, currentFocus?.defaultOpenBreakRows]);

  useEffect(() => {
    if (currentFocus) {
      setLocalPinnedState(currentFocus.currentPin ?? []);
    }
  }, [currentFocus?.focusId]);

  useEffect(() => {
    if (definition.focus.length === 0) {
      loadDatatableDefinition(sjmoCode, tableName, ctrlkey).then(([focus, columns]) => {
        const columnsWithVisibleIsFiltered = columns.map((column) => ({
          ...column,
          hiddenIsFiltered: column.compoVisible === true ? false : true,
        }));

        dispatchDefinition({
          type: "FETCH_DEFINITIONS",
          payload: {
            focus,
            columns: columnsWithVisibleIsFiltered,
            selectedFocus: focus[0].focusId,
          },
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sjmoCode, ctrlkey, tableName]);

  // Enregistre le refresh de la table de suivi auprès de la galaxie afin d'être
  // rafraîchie lors d'un refreshListenerAndCallback (sauvegarde, suppression, process...)
  const [triggerRefresh, setTriggerRefresh] = useState(0);
  const refresh = useCallback(() => {
    setTriggerRefresh((prev) => prev + 1);
  }, []);
  useGalaxieCallbackListener(refresh);

  const onFocusChange = useCallback(
    async (selected: string) => {
      const columnRes = await api.getColumn(sjmoCode, selected);

      dispatchDefinition({
        type: "CHANGE_FOCUS",
        payload: {
          selectedFocus: selected,
          columns: columnRes.data,
        },
      });
    },
    [sjmoCode],
  );

  /**
   * Gestion du déplacement des colonnes
   * L'évènement est envoyé pluseieurs fois lors du drag and drop.
   * On n'enregistre le déplacement qu'au moment où l'utilisateur lache la colonne IE l'index est le même que le précédent.
   */
  const movingColumnIndexRef = useRef<number | null>(null);
  const onColumnMoved = useCallback(
    (event: ColumnMovedEvent) => {
      const columnField = event.column?.getColId();

      // +1 pour la selection et +1 pour les satellites
      const newIndex = (event.toIndex as number) - 2;
      if (columnField && currentFocus && movingColumnIndexRef.current !== newIndex) {
        movingColumnIndexRef.current = newIndex;
      } else if (columnField && currentFocus && movingColumnIndexRef.current === newIndex) {
        // Récupérer la colonne déplacée et son ancien index
        const movedColumn = definition.columns.find((c) => c.column === columnField);
        const oldIndex = definition.columns.findIndex((c) => c.column === columnField);

        if (movedColumn && oldIndex !== -1) {
          // Créer une copie du tableau des colonnes
          const updatedColumns = [...definition.columns];

          // Retirer la colonne de son ancien index
          updatedColumns.splice(oldIndex, 1);

          // Insérer la colonne à son nouvel index
          updatedColumns.splice(newIndex, 0, movedColumn);

          // Dispatcher la nouvelle définition
          dispatchDefinition({
            type: "CHANGE_FOCUS",
            payload: {
              selectedFocus: currentFocus.focusId,
              columns: updatedColumns,
            },
          });
        }
      }
    },
    [currentFocus, definition.columns],
  );

  const onColumnResized = useCallback(
    (params: ColumnResizedEvent) => {
      const columnName = params.column?.getColId();
      const width = params.column?.getActualWidth();

      if (!columnName || !width || !params.finished) return;

      if (currentFocus) {
        const updatedColumns = produce(definition.columns, (draft) => {
          const column = draft.find((c) => c.column === columnName);
          if (column) {
            column.contentSize = width;
            column.labelSize = width;
          }
        });

        dispatchDefinition({
          type: "CHANGE_FOCUS",
          payload: {
            selectedFocus: currentFocus?.focusId,
            columns: updatedColumns,
          },
        });
      }
    },
    [currentFocus, definition.columns],
  );

  const onColumnVisible = useCallback(
    (event: ColumnVisibleEvent) => {
      if (event.visible == null || !currentFocus) return;

      const impactedColumns = (event.columns ?? (event.column ? [event.column] : [])).filter(
        (column) => {
          const columnName = column.getColId();
          return (
            !!columnName &&
            columnName !== "satellite" &&
            !effectiveGroupBy.columns.includes(columnName)
          );
        },
      );
      if (impactedColumns.length === 0) return;

      const visible = event.visible;
      let changed = false;
      const updatedColumns = produce(definition.columns, (draft) => {
        for (const gridColumn of impactedColumns) {
          const draftColumn = draft.find((c) => c.column === gridColumn.getColId());
          if (draftColumn && draftColumn.compoVisible !== visible) {
            draftColumn.compoVisible = visible;
            changed = true;
          }
        }
      });
      if (!changed) return;

      dispatchDefinition({
        type: "CHANGE_FOCUS",
        payload: {
          selectedFocus: currentFocus.focusId,
          columns: updatedColumns,
        },
      });
    },
    [currentFocus, definition.columns, effectiveGroupBy.columns],
  );

  const onSaveFocusPerso = useCallback(
    async (
      {
        currentFilter,
        currentOrderBy,
        currentPinned,
        defaultOpenBreakRows,
      }: {
        currentFilter?: FilterModel;
        currentOrderBy?: SortModelItem[];
        currentPinned?: ColumnState[];
        defaultOpenBreakRows?: boolean;
      },
      callback: () => void,
    ) => {
      if (currentFocus) {
        try {
          await api.saveFocusPersoDatatable(
            currentFocus.focusId,
            tableName,
            ctrlkey,
            sjmoCode,
            definition.columns,
            effectiveGroupBy.columns ?? [],
            currentFilter,
            currentOrderBy,
            currentPinned,
            defaultOpenBreakRows ?? effectiveDefaultOpenBreakRows,
          );
          if (sessionStorage.getItem("superUser") !== "true") {
            const [newFocus, newColumns] = await loadDatatableDefinition(
              sjmoCode,
              tableName,
              ctrlkey,
            );

            const columnsWithVisibleIsFiltered = newColumns.map((column) => ({
              ...column,
              hiddenIsFiltered: column.compoVisible === true ? false : true,
            }));

            dispatchDefinition({
              type: "FETCH_DEFINITIONS",
              payload: {
                focus: newFocus,
                columns: columnsWithVisibleIsFiltered,
                selectedFocus: newFocus[0].focusId,
              },
            });
            setLocalDefaultOpenBreakRows(undefined);
            setLocalGroupByState({ columns: [], functionList: [] });
          } else {
            dispatchDefinition({
              type: "UPDATE_FOCUS_AFTER_SAVE",
              payload: {
                focus: {
                  ...currentFocus,
                  currentFilter: currentFilter,
                  currentOrderBy: currentOrderBy,
                  currentPin: currentPinned,
                  defaultOpenBreakRows: defaultOpenBreakRows,
                },
              },
            });
          }
        } catch (error) {
          console.error(
            "error during the save of the modified focus ",
            currentFocus.focusCode,
            " of the datatable ",
            ctrlkey,
          );
          console.error(error);
        }
        callback();
      }
    },
    [
      currentFocus,
      definition.columns,
      ctrlkey,
      sjmoCode,
      tableName,
      effectiveGroupBy.columns,
      effectiveDefaultOpenBreakRows,
    ],
  );

  const onRazFocusPerso = useCallback(
    async (callback: () => void) => {
      if (currentFocus) {
        await api.razFocusPersoDatatable(currentFocus.focusId, sjmoCode);
        const [newFocus, newColumns] = await loadDatatableDefinition(sjmoCode, tableName, ctrlkey);
        dispatchDefinition({
          type: "FETCH_DEFINITIONS",
          payload: {
            focus: newFocus,
            columns: newColumns,
            selectedFocus: newFocus[0].focusId,
          },
        });
      }
      callback();
    },
    [ctrlkey, currentFocus, sjmoCode, tableName],
  );

  // Tant que la définition (focus + colonnes) n'est pas chargée, on ne monte pas AgGridContainer
  // (cf ticket #6783 / régression chargement depuis une GS).
  if (definition.focus.length === 0) {
    return <div>{t("commun_chargement")}</div>;
  }

  return (
    <AgGridContainer
      ref={gridRef}
      sjmoCode={sjmoCode}
      tableName={tableName}
      processOrigine={tableName}
      ctrlkey={ctrlkey}
      definition={{ type: "DATATABLE", ...definition }}
      currentFocus={currentFocus}
      nodeType={nodeType}
      processIdDrop={processIdDrop}
      groupByState={effectiveGroupBy}
      defaultOpenBreakRows={effectiveDefaultOpenBreakRows}
      additionnalClause={additionnalClause}
      pinnedState={localPinnedState}
      triggerRefresh={triggerRefresh}
      hideFilterBar={false}
      getRSQLFilter={getRSQLFilter}
      fetchData={fetchData}
      onSaveFocusPerso={onSaveFocusPerso}
      onRazFocusPerso={onRazFocusPerso}
      onFocusChange={onFocusChange}
      onColumnMoved={onColumnMoved}
      onColumnVisible={onColumnVisible}
      onRowClickParent={onRowClickParent}
      onColumnResized={onColumnResized}
      onChangeGroupBy={setLocalGroupByState}
      onChangeDefaultOpenBreakRows={setLocalDefaultOpenBreakRows}
      onChangePinned={setLocalPinnedState}
    />
  );
};

export default ReadOnlyTable;
